import type { Notification, NotificationKind } from '@tandemone/types'
import {
  AlertTriangle,
  Award,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  FileCheck,
  FileText,
  MessageSquare,
  Receipt,
  Sparkles,
  UserPlus
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

type NotificationType = NotificationKind['type']

type PayloadOf<T extends NotificationType> = Extract<
  NotificationKind,
  { type: T }
>['payload']

/** recipient context for link resolution. only carries the discriminators
 * that types currently branch on (isSuper today). add fields here only
 * when a real renderer needs them, avoiding speculative coupling.
 */
export type NotificationRecipient = {
  isSuper: boolean
}

type Renderer<T extends NotificationType> = {
  icon: LucideIcon
  title(payload: PayloadOf<T>): string
  description(payload: PayloadOf<T>): string
  /** destination route when the user clicks the row. return null to render
   * the row as non-navigable (no "View" affordance, click only marks read).
   * recipient is passed so multi-audience types (super vs company owner)
   * can branch. see supplier_profile_published below.
   */
  link(payload: PayloadOf<T>, recipient: NotificationRecipient): string | null
}

/** per-type renderer registry. each entry knows how to format the title
 * and description from the typed payload, plus which lucide icon to
 * show and where clicks navigate. add a new notification kind → TS forces
 * you to add an entry here too.
 */
export const notificationRenderers: {
  [K in NotificationType]: Renderer<K>
} = {
  user_signed_up: {
    icon: UserPlus,
    title: payload => `${payload.name || payload.email} signed up`,
    description: payload => payload.email,
    link: payload => `/super/users/${payload.userId}`
  },
  company_created: {
    icon: Building2,
    title: payload => `${payload.companyName} was created`,
    description: () => 'A new company joined the platform',
    /** no super-facing company page until the company picks a type and
     * becomes a supplier/buyer, so the row is informational only.
     */
    link: () => null
  },
  supplier_onboarding_started: {
    icon: Building2,
    title: payload => `${payload.companyName} started supplier onboarding`,
    description: () => 'Review the new supplier profile',
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  buyer_onboarding_started: {
    icon: Building2,
    title: payload => `${payload.companyName} started buyer onboarding`,
    description: () => 'A new buyer just joined',
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  supplier_onboarding_step_completed: {
    icon: ClipboardCheck,
    title: payload => `${payload.companyName} finished ${payload.stepLabel}`,
    description: payload => `Step: ${payload.stepLabel}`,
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  supplier_onboarding_step_updated: {
    icon: ClipboardCheck,
    title: payload => `${payload.companyName} updated ${payload.stepLabel}`,
    description: payload => `Step: ${payload.stepLabel}`,
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  supplier_onboarding_section_completed: {
    icon: CheckCircle2,
    title: payload =>
      `${payload.companyName} completed ${payload.sectionLabel}`,
    description: payload => `Section: ${payload.sectionLabel}`,
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  supplier_onboarding_completed: {
    icon: Sparkles,
    title: payload => `${payload.companyName} completed onboarding`,
    description: () => 'All steps done, ready for review',
    link: (payload, recipient) =>
      recipient.isSuper
        ? `/super/due-diligence/${payload.companySlug}`
        : '/suppliers'
  },
  buyer_onboarding_step_completed: {
    icon: ClipboardCheck,
    title: payload => `${payload.companyName} finished ${payload.stepLabel}`,
    description: payload => `Step: ${payload.stepLabel}`,
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  buyer_onboarding_step_updated: {
    icon: ClipboardCheck,
    title: payload => `${payload.companyName} updated ${payload.stepLabel}`,
    description: payload => `Step: ${payload.stepLabel}`,
    link: payload => `/super/due-diligence/${payload.companySlug}`
  },
  buyer_onboarding_completed: {
    icon: Sparkles,
    title: payload => `${payload.companyName} completed onboarding`,
    description: () => 'Buyer is ready to source',
    link: (payload, recipient) =>
      recipient.isSuper
        ? `/super/due-diligence/${payload.companySlug}`
        : '/suppliers'
  },
  supplier_profile_published: {
    icon: CheckCircle2,
    title: payload => `${payload.companyName} published their profile`,
    description: () => 'Live in the supplier directory',
    link: (payload, recipient) =>
      recipient.isSuper
        ? `/super/due-diligence/${payload.companySlug}`
        : `/suppliers/${payload.companySlug}`
  },
  supplier_certificate_uploaded: {
    icon: Award,
    title: payload =>
      `${payload.companyName} uploaded ${payload.certificateName}`,
    description: payload => payload.certificateName,
    /** supers-only audience and the queue is the dedicated review surface,
     * so route there directly rather than the broader due-diligence detail.
     */
    link: () => '/super/certificate-review'
  },
  billing_payment_failed: {
    icon: AlertTriangle,
    title: payload => `Payment failed for ${payload.planLabel}`,
    description: payload =>
      payload.collectionMethod === 'send_invoice'
        ? 'Your invoice is unpaid. Settle it to keep your plan.'
        : 'We could not charge your card. Update it to keep your plan.',
    link: () => '/billing'
  },
  billing_activation_failed: {
    icon: AlertTriangle,
    title: payload => `${payload.planLabel} could not start`,
    description: payload => {
      if (payload.reason === 'no_payment_method') {
        return 'You are approved, but there was no card on file. You are on Supplier Free until you add one.'
      }
      if (payload.reason === 'card_declined') {
        return 'You are approved, but your card was declined. You are on Supplier Free until it goes through.'
      }
      return 'You are approved, but something went wrong on our side. You are on Supplier Free while we look into it.'
    },
    link: () => '/billing'
  },
  supplier_approved: {
    icon: BadgeCheck,
    title: () => 'Your profile is approved',
    description: () => 'Buyers can now find you and send you RFIs.',
    link: payload => `/suppliers/${payload.companySlug}`
  },
  buyer_approved: {
    icon: BadgeCheck,
    title: () => 'Your account is approved',
    description: () => 'You can now search suppliers and send RFIs.',
    link: () => '/suppliers'
  },
  billing_activated: {
    icon: CheckCircle2,
    title: payload => `${payload.planLabel} is active`,
    description: payload =>
      payload.formattedAmount
        ? `${payload.formattedAmount} paid. Your plan is live.`
        : 'Your plan is live.',
    link: () => '/billing'
  },
  billing_invoice_issued: {
    icon: Receipt,
    title: payload => `Invoice for ${payload.planLabel}`,
    description: payload =>
      `${payload.formattedTotal} due. Your plan starts once it is paid.`,
    /* deliberately not the stripe url on the payload: that one expires, and
     * the billing page mints a fresh link whenever it is opened. */
    link: () => '/billing'
  },
  billing_payment_received: {
    icon: Receipt,
    title: payload => `${payload.formattedAmount} payment received`,
    description: payload => `Your ${payload.planLabel} plan is up to date.`,
    link: () => '/billing?tab=payment-history'
  },
  invoice_issued: {
    icon: Receipt,
    title: payload => `${payload.issuerCompanyName} sent you an invoice`,
    description: payload =>
      `${payload.invoiceNumber} · ${payload.formattedTotal}`,
    link: payload => `/invoices/${payload.invoiceId}`
  },
  rfi_received: {
    icon: FileText,
    title: payload =>
      payload.buyerCompanyName
        ? `${payload.buyerCompanyName} sent you an RFI`
        : 'A buyer sent you an RFI',
    description: payload => payload.projectTitle,
    /* a locked row has nothing useful on the detail page, so it points at the
     * one action that changes that. */
    link: payload =>
      payload.locked && !payload.billedThroughRep
        ? '/billing?upgrade=supplier_pro'
        : `/rfi/${payload.rfiId}`
  },
  company_member_joined: {
    icon: UserPlus,
    title: payload =>
      `${payload.memberName ?? 'A new member'} joined ${payload.companyName}`,
    description: () => 'View your team',
    link: () => '/employees'
  },
  message_received: {
    icon: MessageSquare,
    title: payload => {
      const sender =
        payload.senderUserName ?? payload.senderCompanyName ?? 'Someone'
      return `${sender} sent a message`
    },
    description: payload => {
      if (payload.bodyPreview) return payload.bodyPreview
      if (payload.attachmentCount > 0) {
        return payload.attachmentCount === 1
          ? 'Sent 1 attachment'
          : `Sent ${payload.attachmentCount} attachments`
      }
      return payload.conversationSubject
    },
    link: payload => `/messages/${payload.conversationId}`
  }
}

/** fallback for unknown types (e.g. server adds a type before web ships
 * the renderer). renders a generic "you have a notification" item with
 * no link.
 */
export const fallbackRenderer = {
  icon: FileCheck,
  title: () => 'New notification',
  description: () => 'Open to see details',
  link: () => null
} satisfies {
  icon: LucideIcon
  title: () => string
  description: () => string
  link: () => string | null
}

/** the runtime renderer shape callers see: payloads are widened to unknown
 * because the lookup is keyed on a string `type`, not a discriminant the
 * compiler can narrow. each registry entry still conforms to Renderer<K>.
 */
export type NotificationRenderer = {
  icon: LucideIcon
  title: (payload: unknown) => string
  description: (payload: unknown) => string
  link: (payload: unknown, recipient: NotificationRecipient) => string | null
}

/** resolve the renderer for a notification, falling back to the generic
 * one for kinds the web doesn't have an entry for yet.
 */
export function getNotificationRenderer(
  notification: Notification
): NotificationRenderer {
  const renderer =
    notificationRenderers[
      notification.type as keyof typeof notificationRenderers
    ]
  return (renderer ?? fallbackRenderer) as NotificationRenderer
}
